import { useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import { StarterKit } from "@tiptap/starter-kit";
import { Image } from "@tiptap/extension-image";
import { Link as LinkExt } from "@tiptap/extension-link";
import { TextAlign } from "@tiptap/extension-text-align";
import { Bold, Italic, Underline, Heading2, Heading3, List, ListOrdered, Quote, AlignLeft, AlignCenter, Link2, Globe, Unlink, ImagePlus, Minus, Undo2, Redo2 } from "lucide-react";
import { toast } from "sonner";
import { errMsg, escapeHtml } from "@/lib/api";
import { uploadImage } from "@/admin/ImageField";

// Keeps the Lore Linker's batch marker so "Undo batch" can find its own links
const LoreLink = LinkExt.extend({
  addAttributes() {
    return { ...this.parent?.(), lk: { default: null, parseHTML: (el) => el.getAttribute("data-lk"), renderHTML: (a) => (a.lk ? { "data-lk": a.lk } : {}) } };
  },
});
import { LinkPickerDialog } from "@/admin/LinkPickerDialog";
import { Spoiler } from "@/admin/SpoilerMark";
import { useSite } from "@/context/SiteContext";
import { EyeOff as SpoilerIcon } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const SpoilerDialog = ({ open, onOpenChange, onPick }) => {
  const { series } = useSite();
  const opts = [{ key: "general", label: "General spoiler" }, ...series.flatMap((s) => s.books.map((b) => ({ key: b.id, label: `Book ${b.number}: ${b.title}` })))];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="spoiler-modal" className="bg-[#f4efe2] border-2 border-[#c9a227] rounded-none max-w-md">
        <DialogTitle className="font-display text-2xl">Mark as spoiler</DialogTitle>
        <DialogDescription className="italic">Which book does the selected text spoil? Readers see it hidden until they tap to reveal.</DialogDescription>
        <div className="grid gap-2">
          {opts.map((o) => (
            <button key={o.key} type="button" data-testid={`spoiler-book-${o.key}`} onClick={() => (o.key === "general" ? onPick("", "") : onPick(o.label, o.key))}
              className="text-left px-4 py-2.5 border border-[#c9a227]/60 font-heading text-sm hover:bg-[#991b1b] hover:text-white transition-colors">{o.label}</button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Tb = ({ icon: Icon, label, onClick, active, testid }) => (
  <button type="button" title={label} data-testid={testid} onMouseDown={(e) => e.preventDefault()} onClick={onClick}
    className={`p-2 border transition-colors ${active ? "bg-[#991b1b] text-[#fdf6e3] border-[#c9a227]" : "border-transparent hover:border-[#c9a227] hover:bg-[#c9a227]/15"}`}>
    <Icon className="w-4 h-4" />
  </button>
);

const LinkBtn = ({ icon: Icon, label, onClick, testid }) => (
  <button type="button" data-testid={testid} onMouseDown={(e) => e.preventDefault()} onClick={onClick}
    className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#c9a227] bg-[#fbf7ec] font-heading text-xs font-bold hover:bg-[#c9a227]/20 transition-colors">
    <Icon className="w-3.5 h-3.5" /> {label}
  </button>
);

export const RichEditor = ({ value, onChange }) => {
  const [picker, setPicker] = useState(false);
  const [spoilerOpen, setSpoilerOpen] = useState(false);
  const [selText, setSelText] = useState("");
  const fileRef = useRef(null);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] }, link: false }),
      LoreLink.configure({ openOnClick: false, autolink: true, HTMLAttributes: { target: null, rel: null } }),
      Image,
      Spoiler,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
    ],
    content: value,
    shouldRerenderOnTransaction: true,
    onUpdate: ({ editor: ed }) => onChange(ed.getHTML()),
  });
  if (!editor) return null;
  const c = () => editor.chain().focus();

  const openPicker = () => {
    const { from, to } = editor.state.selection;
    setSelText(editor.state.doc.textBetween(from, to, " "));
    setPicker(true);
  };
  const pick = (t) => {
    if (editor.state.selection.empty) c().insertContent(`<a href="/wiki/${t.slug}">${escapeHtml(t.title)}</a> `).run();
    else c().setLink({ href: `/wiki/${t.slug}` }).run();
    setPicker(false);
  };
  const missing = (title) => {
    const text = editor.state.selection.empty || selText === title ? `[[${title}]]` : `[[${title}|${selText}]]`;
    c().insertContent(escapeHtml(text)).run();
    setPicker(false);
  };
  const external = () => {
    const url = window.prompt("External URL (https://…)", editor.getAttributes("link").href || "https://");
    if (url) c().extendMarkRange("link").setLink({ href: url }).run();
  };
  const addImage = async (file) => {
    if (!file) return;
    try { const url = await uploadImage(file); c().setImage({ src: url }).run(); } catch (e) { toast.error(errMsg(e)); }
    fileRef.current.value = "";
  };

  return (
    <div data-testid="rich-editor" className="vellum tiptap-shell">
      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-1 p-2 border-b border-[#c9a227]/50 bg-[#f4efe2]">
        <Tb icon={Bold} label="Bold" testid="rte-bold" active={editor.isActive("bold")} onClick={() => c().toggleBold().run()} />
        <Tb icon={Italic} label="Italic" testid="rte-italic" active={editor.isActive("italic")} onClick={() => c().toggleItalic().run()} />
        <Tb icon={Underline} label="Underline" testid="rte-underline" active={editor.isActive("underline")} onClick={() => c().toggleUnderline().run()} />
        <Tb icon={Heading2} label="Heading" testid="rte-h2" active={editor.isActive("heading", { level: 2 })} onClick={() => c().toggleHeading({ level: 2 }).run()} />
        <Tb icon={Heading3} label="Subheading" testid="rte-h3" active={editor.isActive("heading", { level: 3 })} onClick={() => c().toggleHeading({ level: 3 }).run()} />
        <Tb icon={List} label="Bullet list" testid="rte-ul" active={editor.isActive("bulletList")} onClick={() => c().toggleBulletList().run()} />
        <Tb icon={ListOrdered} label="Numbered list" testid="rte-ol" active={editor.isActive("orderedList")} onClick={() => c().toggleOrderedList().run()} />
        <Tb icon={Quote} label="Quote" testid="rte-quote" active={editor.isActive("blockquote")} onClick={() => c().toggleBlockquote().run()} />
        <Tb icon={AlignLeft} label="Align left" testid="rte-left" active={editor.isActive({ textAlign: "left" })} onClick={() => c().setTextAlign("left").run()} />
        <Tb icon={AlignCenter} label="Center" testid="rte-center" active={editor.isActive({ textAlign: "center" })} onClick={() => c().setTextAlign("center").run()} />
        <Tb icon={Minus} label="Divider" testid="rte-hr" onClick={() => c().setHorizontalRule().run()} />
        <Tb icon={ImagePlus} label="Insert image" testid="rte-image" onClick={() => fileRef.current?.click()} />
        <Tb icon={Undo2} label="Undo" testid="rte-undo" onClick={() => c().undo().run()} />
        <Tb icon={Redo2} label="Redo" testid="rte-redo" onClick={() => c().redo().run()} />
        <span className="w-px h-6 bg-[#c9a227]/50 mx-1" />
        <LinkBtn icon={Link2} label="Link to page" testid="rte-link-page" onClick={openPicker} />
        <LinkBtn icon={Globe} label="Web link" testid="rte-link-external" onClick={external} />
        {editor.isActive("link") && <LinkBtn icon={Unlink} label="Unlink" testid="rte-unlink" onClick={() => c().extendMarkRange("link").unsetLink().run()} />}
        <LinkBtn icon={SpoilerIcon} label="Spoiler" testid="rte-spoiler" onClick={() => (editor.state.selection.empty ? toast.info("Select the text that contains the spoiler first") : setSpoilerOpen(true))} />
        {editor.isActive("spoiler") && <LinkBtn icon={Unlink} label="Remove spoiler" testid="rte-unspoiler" onClick={() => c().extendMarkRange("spoiler").unsetMark("spoiler").run()} />}
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => addImage(e.target.files?.[0])} />
      </div>
      <EditorContent editor={editor} className="codex-prose" data-testid="rich-editor-content" />
      <LinkPickerDialog open={picker} onOpenChange={setPicker} onPick={pick} onMissing={missing} selectedText={selText} />
      <SpoilerDialog open={spoilerOpen} onOpenChange={setSpoilerOpen} onPick={(book, bookId) => { c().setMark("spoiler", { book, bookId }).run(); setSpoilerOpen(false); }} />
    </div>
  );
};
